/**
 * EntityShapeView
 *
 * The friendly entity page, one template for every class, drawn from the SHACL view shapes:
 * identity, a side menu of topics (one per sh:PropertyGroup) beside a pinned hero panel (along
 * the track, as a graph, on the map) that follows the topic, the facts of the topic with the lens
 * switch, and about the record. Widgets come from dash:viewer and read other rows by dview:key.
 * Triples no shape covers are facts too: RINF's era: terms and the other properties.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@bn-apps/dim-ui-react';
import { ChevronDown, ChevronUp } from '@bn-apps/design-system-icons';
import { useResolvedDisplayValues } from '@hooks/useResolvedDisplayValues';
import { useEraLabels, useRinfLabels } from '@hooks/useEraLabels';
import { useIncomingLinks } from '@hooks/useIncomingLinks';
import { useConcepts } from '@hooks/useConcepts';
import { useValidationResults } from '@hooks/useValidationResults';
import type { EntityViewModel } from '@hooks/useEntityView';
import { DVIEW, heroKeys, isLinearLocationViewer, setting } from '@utils/viewShapes.utils';
import type { PrefixMap } from '@utils/prefix.utils';
import type { EntityProperty } from '@/types';
import { ERA, focusIris, pageIris, resultsByRow, validityOf } from './page';
import { buildLinearLocation } from './linearLocation';
import type { Lens, WidgetContext } from './widgets/types';
import About from './sections/About';
import Connected from './sections/Connected';
import Facts, { factGroups, topicOf } from './sections/Facts';
import TopicMenu from './sections/TopicMenu';
import Hero, { type HeroView } from './sections/Hero';
import Identity from './sections/Identity';
import Triples from './sections/Triples';
import { LENSES, LensSwitch } from './sections/LensBar';
import { useStored } from './stored';
import styles from './EntityTemplate.module.scss';

interface EntityShapeViewProps {
  model: EntityViewModel;
  entityProperties: EntityProperty[];
  prefixes: PrefixMap;
  label: string;
  wkt: string | null;
  /** The Overview / Technical switch, shown beside the breadcrumbs. */
  viewSwitch?: ReactNode;
}

const LENS_KEY = 'dim-entity-lens';
const HERO_KEY = 'dim-entity-hero';
// Topics whose group was renamed: old links and remembered topics still open them
const RENAMED_TOPICS: Record<string, string> = { energy: 'electrification' };

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key) as T | null;
    return value && allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // The choice still applies to this page visit.
  }
}

const EntityShapeView = ({
  model,
  entityProperties,
  prefixes,
  label,
  wkt,
  viewSwitch,
}: EntityShapeViewProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [lens, setLensState] = useState<Lens>(() => readStored(LENS_KEY, LENSES, 'default'));
  const [query, setQuery] = useState('');
  const [changing, setChanging] = useState(false);
  // The reader's last hero view and lens open the next page the same way
  const [heroView, setHeroViewState] = useState<HeroView | null>(() => readStored<HeroView | ''>(HERO_KEY, ['along', 'graph', 'map', 'model', ''], '') || null);
  const setHeroView = (next: HeroView) => {
    setHeroViewState(next);
    store(HERO_KEY, next);
  };
  const [heroSmall, setHeroSmall] = useStored('dim-entity-hero-small', false);
  // The side menu's topic: the link's ?topic= wins, else the last topic read on any entity page
  const [lastTopic, setLastTopic] = useStored('dim-entity-topic', 'overview');
  const [topic, setTopicState] = useState(() => {
    const asked = new URLSearchParams(window.location.search).get('topic') ?? lastTopic;
    return RENAMED_TOPICS[asked] ?? asked;
  });
  const setTopic = (next: string) => {
    setTopicState(next);
    setLastTopic(next);
    // A new topic starts at its top: the facts' title under the pinned hero
    window.requestAnimationFrame(() => {
      const facts = document.getElementById('entity-facts') ?? dock.current;
      if (facts && facts.getBoundingClientRect().top < 0) facts.scrollIntoView({ block: 'start' });
    });
    const url = new URL(window.location.href);
    if (next === 'overview') url.searchParams.delete('topic');
    else url.searchParams.set('topic', next);
    window.history.replaceState(window.history.state, '', url);
  };

  const setLens = (next: Lens) => {
    setLensState(next);
    store(LENS_KEY, next);
    setChanging(true);
  };

  useEffect(() => {
    if (!changing) return;
    const timer = window.setTimeout(() => setChanging(false), 320);
    return () => window.clearTimeout(timer);
  }, [changing, lens]);

  const root = useRef<HTMLDivElement>(null);

  // The pinned hero's height, so a jump to a row lands below it. It keeps one height while the page
  // scrolls (shrinking it then made the page jump); the minimise button makes it small.
  const dock = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = root.current;
    const box = dock.current;
    if (!element || !box) return;
    const measure = () => element.style.setProperty('--hero-dock-h', `${box.getBoundingClientRect().height}px`);
    const sizes = new ResizeObserver(measure);
    sizes.observe(box);
    measure();
    return () => sizes.disconnect();
  }, []);

  const validation = useValidationResults(useMemo(() => focusIris(model), [model]));
  const incoming = useIncomingLinks(model.uri);
  // The page's names at once; the classes of the incoming links in a second, small query when they arrive
  const iris = useMemo(() => pageIris(model), [model]);
  const incomingClasses = useMemo(
    () => [...new Set(incoming.groups.map((group) => group.classIri))].filter((iri) => !iris.includes(iri)),
    [incoming.groups, iris]
  );
  const ownLabels = useResolvedDisplayValues(iris, language);
  const classLabels = useResolvedDisplayValues(incomingClasses, language);
  const pageLabels = useMemo(() => ({ ...classLabels, ...ownLabels }), [classLabels, ownLabels]);
  const eraLabels = useEraLabels(language);

  const concepts = useConcepts();
  const stretch = useMemo(() => {
    const viewer = model.allProperties.find((property) => isLinearLocationViewer(property.viewer));
    return viewer ? buildLinearLocation(viewer, { ...model, concepts }) : null;
  }, [model, concepts]);

  const rinf = useMemo(
    () => new Map(entityProperties.filter((property) => property.predicate.startsWith(ERA)).map((property) => [property.predicate, property.objects])),
    [entityProperties]
  );
  const eraIris = useMemo(
    () =>
      entityProperties.flatMap((property) =>
        property.objects.filter((object) => object.type === 'uri' && object.value.startsWith(ERA)).map((object) => object.value)
      ),
    [entityProperties]
  );
  const rinfLabels = useRinfLabels(eraIris, eraLabels);
  // The page's own name from /describe, for visitors whose name lookups are not allowed
  const resolved = useMemo(
    () => ({ ...rinfLabels, ...eraLabels, ...(label ? { [model.uri]: [label] } : {}), ...pageLabels }),
    [rinfLabels, eraLabels, label, model.uri, pageLabels]
  );
  // The same-as identifiers (dview:sameAs).
  const rowValues = (name: string) => model.allProperties.filter((row) => setting(row, name)).flatMap((row) => model.values[row.id] ?? []);
  const designators = rowValues('sameAs').map((value) => resolved[value.value]?.[0] ?? '').filter(Boolean);
  const heroRowKeys = heroKeys(model.nodeShapes);
  const heroViewers = model.allProperties.filter((row) => !heroRowKeys || (row.key && heroRowKeys.includes(row.key))).map((row) => row.viewer);
  const locationKind = heroViewers.some((viewer) => viewer === DVIEW.AreaLocationViewer)
    ? 'area'
    : heroViewers.some(isLinearLocationViewer)
      ? 'linear'
      : heroViewers.some((viewer) => viewer === DVIEW.SpotLocationViewer || viewer === DVIEW.SpotViewer)
        ? 'spot'
        : null;
  const hasGraph = model.allProperties.some((property) => property.viewer === DVIEW.TrackGraphViewer);
  const openGraph = () => {
    setHeroView('graph');
    document.getElementById('entity-hero')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const baseContext: WidgetContext = {
    language,
    prefixes,
    resolved,
    nestedShape: null,
    nestedValues: model.nestedValues,
    model,
    stretch,
    lens,
    results: validation.results,
    pictures: true,
    rinf,
    concepts,
  };

  const rowResults = useMemo(() => resultsByRow(model, validation.results), [model, validation.results]);
  const groups = factGroups(
    model,
    baseContext,
    rowResults,
    entityProperties,
    { other: t('entity.template.facts.other'), rinf: 'RINF', details: t('entity.view.details') },
    lens === 'quality'
  );
  const picked = groups.find((group) => topicOf(group) === topic);
  const shownTopic = picked || ['all', 'connected', 'triples'].includes(topic) ? topic : 'overview';
  // A search looks in the facts: from a data view it goes back to them, and the facts open the topic it found
  useEffect(() => {
    if (query.trim() && (topic === 'connected' || topic === 'triples')) setTopic('overview');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  // The hero shows the picked topic's lanes; the objects on it stay lit for the contents topic
  const context: WidgetContext = {
    ...baseContext,
    // Overview: every lane, no objects; the objects show on the topic that holds them
    topic: picked
      ? { lanes: picked.lanes ?? [], objects: picked.domain === 'contents', kinds: picked.kinds ?? [] }
      : shownTopic === 'overview'
        ? { lanes: [], objects: false, kinds: [], overview: true }
        : null,
  };
  const validity = validityOf(entityProperties);
  const warnings = validation.results.length;

  return (
    <div ref={root} className={styles.template} data-lens={lens} data-changing={changing || undefined}>
      <Identity model={model} label={label} context={context} validity={validity} stretch={stretch} warnings={warnings} viewSwitch={viewSwitch} />
      <div className={styles.topicLayout}>
        <TopicMenu groups={groups} topic={shownTopic} onTopic={setTopic} query={query} onQuery={setQuery} />
        <div className={styles.topicMain}>
          {/* Pinned while the topic scrolls under it; it can be made small */}
          <div ref={dock} className={styles.heroDock} data-small={heroSmall || undefined}>
            <Hero
              model={model}
              context={context}
              wkt={wkt}
              chosen={heroView}
              onChoose={setHeroView}
              incoming={incoming.groups}
              actions={
                <Button
                  variant="tertiary"
                  size="xs"
                  hideText
                  iconStart={heroSmall ? ChevronDown : ChevronUp}
                  aria-expanded={!heroSmall}
                  onClick={() => setHeroSmall(!heroSmall)}
                >
                  {heroSmall ? t('entity.template.topics.heroLarger') : t('entity.template.topics.heroSmaller')}
                </Button>
              }
            />
          </div>
          <div className={styles.lensLayer}>
            {/* The data views of the side menu: everything as text, what it links to, the raw triples */}
            {shownTopic === 'connected' ? (
              <Connected
                uri={model.uri}
                entityProperties={entityProperties}
                incoming={incoming}
                context={context}
                designators={designators}
                onOpenGraph={hasGraph ? openGraph : null}
              />
            ) : shownTopic === 'triples' ? (
              <Triples uri={model.uri} properties={entityProperties} prefixes={prefixes} />
            ) : (
              <Facts
                groups={groups}
                context={context}
                rinf={rinf}
                view={shownTopic === 'all' ? 'plain' : 'grouped'}
                topic={shownTopic}
                onTopic={setTopic}
                query={query}
                onQuery={setQuery}
                lensSwitch={<LensSwitch lens={lens} onChange={setLens} />}
              />
            )}
            {model.unavailable.size > 0 && (
              <p className={styles.cardNote}>{t('entity.view.unavailable', { count: model.unavailable.size })}</p>
            )}
          </div>
        </div>
      </div>
      <About uri={model.uri} validity={validity} nodeShapes={model.nodeShapes} language={language} kind={locationKind} />
    </div>
  );
};

export default EntityShapeView;
